import { useCallback, useContext, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslations } from 'next-intl';
import dayjs from 'dayjs';
import remarkDirective from 'remark-directive';
import remarkDirectiveRehype from 'remark-directive-rehype';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { ChatbotContext } from './chatbot-context';
import { ChatbotCitationDocument } from './types';
import { navigateCitedDocument, trimCitationDocumentAttachments } from './util';

interface CitationContentProps {
  doc: ChatbotCitationDocument;
  currentPortalId?: string;
}

export function CitationContent({ doc, currentPortalId }: CitationContentProps) {
  const content = useMemo(() => {
    return doc.content ? trimCitationDocumentAttachments(doc.content) : '';
  }, [doc.content]);

  const citationContent = useMemo(() => {
    return (
      <ReactMarkdown
        className="line-clamp-4 not-prose text-xs font-normal text-neutral-500"
        remarkPlugins={[remarkDirective, remarkDirectiveRehype]}
        components={{
          h1: ({ node, children, ...props }) => {
            return (
              <h1 className="mb-2" {...props}>
                {children}
              </h1>
            );
          },
          h2: ({ node, children, ...props }) => {
            return (
              <h2 className="mt-0 mb-1" {...props}>
                {children}
              </h2>
            );
          },
          h3: ({ node, children, ...props }) => {
            return (
              <h2 className="mt-0 mb-1" {...props}>
                {children}
              </h2>
            );
          },
          span: ({ node, children, ...props }) => {
            if ('data-tag-role' in props) {
              return <span className="text-neutral-800 float-left mr-1 text-xs font-semibold">{children}</span>;
            }
            return <span {...props} children={children} />;
          },
          iframe: ({ node, children, ...props }) => {
            return <iframe className="max-w-full" title={doc.title} {...props} />;
          },
        }}
      >
        {doc.type === 'page'
          ? (doc.updatedAt ? `:span[${dayjs.unix(doc.updatedAt).format('DD MMM YYYY')}]{data-tag-role="date"}\n` : '') +
            content
          : content}
      </ReactMarkdown>
    );
  }, [content, doc.title, doc.type, doc.updatedAt]);

  return (
    <div
      className="relative flex flex-col bg-white p-3 rounded-lg w-[264px] z-20"
      style={{ boxShadow: '0px 2px 15px 0px #A3A8AD38' }}
    >
      {doc.type === 'page' || doc.type === 'portalPage' ? (
        <InternalPageCitationPopover doc={doc} text={citationContent} currentPortalId={currentPortalId} />
      ) : doc.type === 'guide' ? (
        <GuideCitationPopover doc={doc} text={citationContent} />
      ) : (
        <OtherCitationPopover doc={doc} />
      )}
    </div>
  );
}

function InternalPageCitationPopover({ doc, text, currentPortalId }: CitationContentProps & { text: JSX.Element }) {
  const aiContext = useContext(ChatbotContext);
  const onButtonLinkClick = useCallback(() => {
    navigateCitedDocument(doc, aiContext, currentPortalId);
  }, [aiContext, currentPortalId, doc]);

  return (
    <>
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center">
          <h3 className="font-semibold text-sm m-0">{doc.title}</h3>
        </div>
        <button className="text-neutral-500" onClick={onButtonLinkClick}>
          <Icon icon={IconsMap.EXTERNAL_LINK} className="w-4" />
        </button>
      </div>
      <div className="flex items-top w-full gap-2">
        {text}
        {doc.pagePreviewUrl ? (
          <div className="w-16 h-16 rounded-lg overflow-hidden flex-none">
            <img className="object-cover w-16 h-16" alt={doc.title} src={doc.pagePreviewUrl} />
          </div>
        ) : null}
      </div>
    </>
  );
}

function GuideCitationPopover({ doc, text }: { doc: ChatbotCitationDocument; text: JSX.Element }) {
  const t = useTranslations('features');
  const { guideCategory, guideCategoryUrl } = doc;

  return (
    <>
      <div className="flex justify-between items-start mb-3">
        <h3 className="font-semibold text-sm m-0">{doc.title}</h3>
        <a href={doc.url} rel="noreferrer" target="_blank" className="text-neutral-500">
          <Icon icon={IconsMap.EXTERNAL_LINK} className="w-4" />
        </a>
      </div>
      {text}
      <div className="bg-neutral-200 w-full h-[1px] my-2.5"></div>
      <div className="flex items-center gap-1 text-neutral-500 text-xs">
        <a className="underline text-neutral-500" href="https://nimbusweb.me/guides/" rel="noreferrer" target="_blank">
          {t('ai-helper.guides')}
        </a>
        <span>•</span>
        {guideCategory && guideCategoryUrl ? (
          <a className="underline text-neutral-500" href={guideCategoryUrl} rel="noreferrer" target="_blank">
            {guideCategory}
          </a>
        ) : (
          <a
            className="underline text-neutral-500"
            href="https://nimbusweb.me/guides/getting-started/"
            rel="noreferrer"
            target="_blank"
          >
            {t('ai-helper.getting_started')}
          </a>
        )}
      </div>
    </>
  );
}

function OtherCitationPopover({ doc }: { doc: ChatbotCitationDocument }) {
  const url = new URL(doc.url);
  return (
    <>
      <div className="flex justify-between items-start mb-3">
        <h3 className="font-semibold text-[13px] m-0 line-clamp-3">{doc.title}</h3>
        <a href={doc.url} rel="noreferrer" target="_blank" className="text-neutral-500">
          <Icon icon={IconsMap.EXTERNAL_LINK} className="w-4" />
        </a>
      </div>
      <div className="bg-neutral-200 w-full h-[1px] my-2.5"></div>
      <a className="underline text-neutral-500" href={url.origin} rel="noreferrer" target="_blank">
        {url.host}
      </a>
    </>
  );
}
